import React, { useContext, useMemo, useState } from "react";
import "./ProductListLayout.css";
import { useSnackbar } from "../../layouts/ecommerce/product/hooks/SnackbarProvider";
import { UserContext } from "../../App";
import { Modal, Box, CircularProgress } from "@mui/material";
import { getStoreCurrencySymbol } from "../../utils/currency";

const isItemAvailable = (itemAvailabilities) => {
  if (!itemAvailabilities || itemAvailabilities.length === 0) return true;
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  return itemAvailabilities.some(({ startTime, endTime }) => {
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    return currentMinutes >= sh * 60 + sm && currentMinutes <= eh * 60 + em;
  });
};

const formatAvailabilityTimes = (itemAvailabilities) => {
  if (!itemAvailabilities || itemAvailabilities.length === 0) return "";
  return itemAvailabilities.map(({ startTime, endTime }) => `${startTime} - ${endTime}`).join(", ");
};

const getRestrictOosConfig = () => {
  try {
    const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
    const flag = appConfig?.all?.RESTRICT_OOS_ORDERING?.flag;
    return flag === false ? false : true;
  } catch {
    return true;
  }
};

const getNotServingLabel = (itemAvailabilities) => {
  if (!itemAvailabilities?.length) return "Not serving now";
  const slot = itemAvailabilities[0];
  if (!slot?.endTime) return "Not serving now";
  const fmt = (t) => {
    const [h, m] = t.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`;
  };
  return `Not serving now · menu until ${fmt(slot.endTime)}`;
};

export const ProductListLayout = ({
  title,
  price,
  img,
  alt,
  onClick,
  onTileClick,
  loading,
  shortCode,
  foodType,
  complimentaryMode,
  isBundle,
  bundleVariants,
  itemAvailabilities,
  hasMultipleVariants,
  isOutOfStock,
}) => {
  const showSnackbar = useSnackbar();
  const { color } = useContext(UserContext);
  const retailType = localStorage.getItem("retailType");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isAvailable = useMemo(() => isItemAvailable(itemAvailabilities), [itemAvailabilities]);
  const availabilityText = useMemo(
    () => formatAvailabilityTimes(itemAvailabilities),
    [itemAvailabilities]
  );
  const notServingLabel = useMemo(
    () => getNotServingLabel(itemAvailabilities),
    [itemAvailabilities]
  );
  const restrictOos = useMemo(() => getRestrictOosConfig(), []);

  const handleProceedAnyway = () => {
    setShowConfirmModal(false);
    onClick();
  };
  const handleCancelAdd = () => setShowConfirmModal(false);

  const handleButtonClick = () => {
    if (complimentaryMode) {
      showSnackbar("Please disable the complimentary mode", "warning");
      return;
    }
    const cartId = localStorage.getItem("CartId");
    if (!cartId) {
      showSnackbar("Please 'ADD' or 'SKIP' customer to add products", "error");
      return;
    }
    if (restrictOos) {
      if (isOutOfStock) {
        showSnackbar("This item is currently out of stock", "warning");
        return;
      }
      if (!isAvailable) {
        setShowConfirmModal(true);
        return;
      }
    }
    onClick();
  };

  return (
    <>
      <div
        className={`pll-compact-card ${color === "dark" ? "dark" : ""}${
          isOutOfStock && restrictOos && !onTileClick ? " pll-card-disabled" : ""
        }`}
        onClick={onTileClick ?? (isOutOfStock && restrictOos ? undefined : handleButtonClick)}
        style={{ cursor: isOutOfStock && restrictOos && !onTileClick ? "not-allowed" : "pointer" }}
      >
        {loading && (
          <div className="pll-loader-overlay">
            <CircularProgress size={16} sx={{ color: "#0860E6" }} />
          </div>
        )}

        {retailType === "RESTAURANT" && foodType && (
          <span
            className="pll-food-dot"
            style={{
              background:
                foodType === "VEG"
                  ? "#16a34a"
                  : foodType === "NON_VEG"
                  ? "#dc2626"
                  : foodType === "VEGAN"
                  ? "#15803d"
                  : foodType === "EGG"
                  ? "#ca8a04"
                  : "#9ca3af",
            }}
            title={foodType}
          />
        )}

        <p className="pll-name">{title || "-"}</p>

        {isBundle && bundleVariants?.length > 0 && (
          <p className="pll-includes-text">Includes {bundleVariants.length} items</p>
        )}

        {isOutOfStock ? (
          <p className="pll-status-text">Out of stock</p>
        ) : !isAvailable ? (
          <p className="pll-status-text">{notServingLabel}</p>
        ) : hasMultipleVariants ? (
          <p className="pll-variants">More Variants</p>
        ) : price ? (
          <p className="pll-price">{typeof price === "number" ? `${getStoreCurrencySymbol()} ${price}` : price}</p>
        ) : null}

        {/* Separate ADD button — only shown when tile body opens drawer */}
        {onTileClick && (
          <button
            className="pll-add-btn"
            disabled={isOutOfStock && restrictOos}
            aria-label={`Add ${title}`}
            onClick={(e) => {
              e.stopPropagation();
              handleButtonClick();
            }}
          >
            +
          </button>
        )}
      </div>

      <Modal open={showConfirmModal} onClose={handleCancelAdd}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            maxWidth: "90%",
            bgcolor: color === "dark" ? "#353a50" : "white",
            color: color === "dark" ? "white" : "black",
            boxShadow: 24,
            borderRadius: "8px",
            p: 3,
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: "1rem", fontSize: "1.25rem" }}>
            Item Not Available
          </h3>
          <p style={{ marginBottom: "0.5rem", fontSize: "0.938rem" }}>
            <strong>{title}</strong> is not available at this time.
          </p>
          <p style={{ marginBottom: "1.5rem", fontSize: "0.875rem", color: "#ff3b30" }}>
            Available during: {availabilityText}
          </p>
          <p style={{ marginBottom: "1.5rem", fontSize: "0.938rem" }}>
            Do you want to proceed adding this product to the cart?
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              onClick={handleCancelAdd}
              style={{
                padding: "10px 20px",
                backgroundColor: color === "dark" ? "#48484a" : "#e0e0e0",
                color: color === "dark" ? "white" : "black",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.938rem",
                fontWeight: 600,
              }}
            >
              No
            </button>
            <button
              onClick={handleProceedAnyway}
              style={{
                padding: "10px 20px",
                backgroundColor: "#0860E6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.938rem",
                fontWeight: 600,
              }}
            >
              Yes, Add Anyway
            </button>
          </div>
        </Box>
      </Modal>
    </>
  );
};
